/**
 * The Loading component, used while resources load
 * Keep this component as similar as possible to the resource page importing it
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  Button,
  TabContent,
  TabPane,
  Label,
  FormGroup,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import {
  upload as uploadFile,
  uploadSuccess,
  reset as resetFileUpload,
  startCrop,
  endCrop,
  resetUploadFail,
} from "../../redux/cop/artworks";

import OrderTab from "./utils/OrderTab";
import MaterialTab from "./utils/MaterialTab";
import CuttingTab from "./utils/CuttingTab";
import SizeTab from "./utils/SizeTab";
import SizeSelectTab from "./utils/SizeSelectTab";
import FileTab from "./utils/FileTab";
import DataLayerHelper from "../../utils/dataLayer";
import OtherOptionsTab from "./utils/OtherOptionsTab";
import {
  FILEEDITOR,
  FILEEDITORTYPENAMES,
} from "../FileUploadEditorForm/utils/fileEditor";
import EditorFormHelpers from "../FileUploadEditorForm/utils/FileUploadEditorFormHelpers";
import FormattedNumber from "../FormattedNumber";
import { FormProvider, useForm, useWatch, Controller } from "react-hook-form";
import fileguideIcon from "../../assets/images/icons/fileguide_colored.svg";
import AddToCartModal from "../CheckoutCart/utils/AddToCartModal";

import { addToCart } from "../../redux/editor/shop";
import {
  addItemToCart,
  resetCartState as resetCart,
} from "../../redux/shop/cart";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { isServer } from "../../redux/store";
import {
  FaCaretDown,
  FaCaretLeft,
  FaCaretRight,
  FaCaretUp,
  FaExclamationTriangle,
  FaTimes,
} from "react-icons/fa";
import PaymentMethodBadges from "../ShopProduct/utils/PaymentMethodBadges";
import { createProject, resetCreateFail } from "../../redux/cop/projects";
import { REACT_APP_COP_SERVER } from "../../utils/env";

const InproductConfigurator = (
  { productTitle, variantId, editorTypeId, scrollToConfigurator },
  context
) => {
  const [formTouched, setFormTouched] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [showSmallHintModal, setShowSmallHintModal] = useState(false);
  // const [ignoreSmallHint, setIgnoreSmallHint] = useState(true);

  const shopCart = useSelector((state) => {
    return state.shopCart;
  });
  const file = useSelector((state) => {
    return state.editorFile;
  });
  const language = useSelector((state) => {
    return state.i18nState.lang;
  });

  const {
    cart,
    addingToCart,
    addedToCart,
    addToCartError,
    addToShopCartSuccess,
    addToCartProcessing,
  } = shopCart;

  const editorData = FILEEDITOR[editorTypeId];

  const formDefaultValues = {
    width: editorData.sections.find((section) => section === "sizeselect")
      ? editorData?.availableSizes[0]?.sizes[0][0] || 100
      : 100,
    height: editorData.sections.find((section) => section === "sizeselect")
      ? editorData?.availableSizes[0]?.sizes[0][1] || 100
      : 100,
    laminate: editorData.sections.find((section) => section === "laminate")
      ? "glossy"
      : "none",
    cutting: "rectangular",
    filecheck_checkbox: true,
    skip_file_upload: true,
    amount: 1,
    sku: null,
  };

  const methods = useForm({
    defaultValues: formDefaultValues,
  });
  const { handleSubmit, control, setValue, reset, watch } = methods;

  const formData = useWatch({ control });

  useEffect(() => {
    try {
      if (
        !formTouched &&
        JSON.stringify(formData) !== JSON.stringify(formDefaultValues)
      )
        setFormTouched(true);
    } catch (e) {
      console.log("ERROR", "Failed to stringify Form Data", e);
    }
  }, [formData]);

  useEffect(() => {
    if (formTouched) DataLayerHelper.addEvent("inproduct_editor_start", "text");
  }, [formTouched]);

  // Form Data should be used to render stuff

  let stepIdCounter = 1;
  const steps = [];
  editorData.sections.forEach((section) => {
    let sectionType = section;
    if (section === "laminate") sectionType = "material";
    if (
      section === "filecheck" ||
      section === "border" ||
      section === "underglass"
    )
      return;
    steps.push({
      id: stepIdCounter++,
      type: sectionType,
      title: sectionType === "sizeselect" ? "select size" : sectionType,
    });
  });

  steps.push({
    id: stepIdCounter++,
    type: "file",
    title: "production data" || context.t("production data"),
  });
  steps.push({ id: stepIdCounter++, type: "order", title: "amount" });

  const itemPrice = EditorFormHelpers.calculatePrice({
    ...formData,
    editorType: editorTypeId,
    border: 0,
    width: parseInt(formData.width),
    height: parseInt(formData.height),
  });

  const continueToNextStep = () => {
    if (activeTab + 1 === steps?.length + 1) {
      setSummaryOpen(true);
    }
    setActiveTab(activeTab + 1);
  };
  const continueToPreviousStep = () => {
    setActiveTab(activeTab - 1);
  };

  const dispatch = useDispatch();

  const resetForm = () => {
    setActiveTab(1);
    setSummaryOpen(false);
    setShowSmallHintModal(false);
    dispatch(resetCart());
    dispatch(resetFileUpload());
    dispatch(resetCreateFail());
    reset(formDefaultValues);
    setValue("sku", EditorFormHelpers.generateSKU("designer"));
  };

  const updateSizes = (width, height) => {
    setValue("width", width);
    setValue("height", height);
  };

  const translations = {
    title: context.t("Text"),
    yes: context.t("Yes"),
    no: context.t("No"),
    width: context.t("Width"),
    height: context.t("Height"),
    laminate: context.t("Laminate"),
    cutting: context.t("Cutting"),
    material: context.t("Material"),
    color: context.t("Color"),
    border: context.t("Border"),
    underglass: context.t("Underglass"),
    filecheck: context.t("File check"),
    lines: context.t("Lines"),
    glossy: context.t("GLOSSY"),
    matt: context.t("MATT"),
    neon: context.t("NEON"),
    frosted: context.t("FROSTED"),
    none: context.t("none"),
    rectangular: context.t("rectangular"),
    outline: context.t("outline"),
    exempted: context.t("exempted"),
    whiteprint_partial: context.t(
      "Partial white printing (CMYK + spot color white)"
    ),
    whiteprint_full: context.t("Full white print (CMYK + spot color white)"),
    whiteprint_onlywhite: context.t("White print only (special color white)"),
    stripwidth: context.t("Width of Strips: 70cm (max)"),
    skip_file_upload: context.t("File Upload after Order"),
    missingSection: context.t(
      "Missing section: Please contact customer service: kontakt@myfolie.com - "
    ),
  };

  const showMightBeDevidedHint =
    FILEEDITOR[editorTypeId] &&
    FILEEDITOR[editorTypeId].boundaries &&
    FILEEDITOR[editorTypeId].boundaries.maxNoDevide &&
    parseInt(formData.height) >
      FILEEDITOR[editorTypeId].boundaries.maxNoDevide &&
    parseInt(formData.width) > FILEEDITOR[editorTypeId].boundaries.maxNoDevide;

  const defaultWarningSize = 120;
  // Disable Hint because it looks too aggresive since default values already fall into this
  const showSmallHint =
    (formData.width <
      (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
        ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
        : defaultWarningSize) &&
      formData.height <
        (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
          ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
          : defaultWarningSize)) ||
    formData.width <
      (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
        ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
        : defaultWarningSize) /
        2 ||
    formData.height <
      (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
        ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
        : defaultWarningSize) /
        2;
  const smallDevice = !isServer
    ? window.matchMedia("(max-width: 767px)").matches
    : false;

  const formState = watch();
  const artworkId = watch("artworkId");
  const {
    artwork,
    currentArtworkId,
    cropping,
    projectCreateError,
    artworkUploadError,
  } = useSelector((state) => ({
    artwork: state.artworks.byIdDictionary[artworkId],
    currentArtworkId: state.artworks.currentArtworkId,
    cropping: state.artworks.cropping,
    projectCreateError: state.projects.createError,
    artworkUploadError: state.artworks.uploadError,
  }));

  // Function to create or save project and add to card. tbd save as draft
  const onSubmit = (data) => {
    const itemSku = formData.sku || EditorFormHelpers.generateSKU("designer");
    const json = EditorFormHelpers.generateJson(
      {
        ...data,
        editorType: editorTypeId,
        border: 0,
        width: parseInt(data.width),
        height: parseInt(data.height),
      },
      language,
      translations,
      artwork,
      itemSku,
      cropping
    );

    // const json = EditorFormHelpers.generateJson(
    //   formState,
    //   language,
    //   translations,
    //   file,
    //   sku
    // );

    const parsedEditorType = parseInt(editorTypeId);
    const availableSizes =
      FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
        ? FILEEDITOR[parsedEditorType].availableSizes
        : [];
    const sections =
      FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
        ? FILEEDITOR[parsedEditorType].sections
        : [];

    if (
      isNaN(json.width) ||
      isNaN(json.height) ||
      json.width < 10 ||
      json.height < 10
    ) {
      // toggleAddToCartErrorModal();
      alert("toggleAddToCartErrorModal(sizes to small)");
      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "sizes to small"
      );
    } else if (!artwork?.data?.attributes?.width && !data.skip_file_upload) {
      // toggleAddToCartErrorModal();
      console.log(
        "debug",
        artwork?.data,
        artwork?.data?.attributes?.width,
        !data.skip_file_upload
      );
      alert("toggleAddToCartErrorModal(file missing)");
      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "file missing"
      );
    } else if (
      sections.includes("sizeselect") &&
      !availableSizes.find((availableSizeObj) => {
        return availableSizeObj.sizes.find((availableSize) => {
          return (
            availableSize[0] === json.width && availableSize[1] === json.height
          );
        });
      })
    ) {
      // toggleAddToCartErrorModal();
      alert("toggleAddToCartErrorModal(unavailable size)");

      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "unavailable size"
      );
    } else if (
      FILEEDITOR[parsedEditorType]?.showDefaultAmounts &&
      json.amount < FILEEDITOR[parsedEditorType]?.discounts[0].amount
    ) {
      // toggleAddToCartErrorModal();
      alert("toggleAddToCartErrorModal(minimum amount unmatched)");

      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "minimum amount unmatched"
      );
    } else {
      let configurationDescription = "";
      configurationDescription += `${context.t("material")}: ${context.t(
        "materials." + FILEEDITORTYPENAMES[editorTypeId] + ".title"
      )}`;
      if (editorData.sections.find((section) => section === "laminate"))
        configurationDescription += `, ${context.t(
          "laminates." + data?.laminate + ".title"
        )}`;
      configurationDescription += `; `;
      configurationDescription += `${context.t("Size")}: ${data?.width}mm x ${
        data?.height
      }mm; `;
      configurationDescription += `${context.t("Cutting")}: ${context.t(
        data?.cutting
      )}; `;
      if (data.skip_file_upload)
        configurationDescription += `${context.t("File Upload after Order")}; `;

      dispatch(
        createProject({
          project: {
            uid: json.sku,
            artwork_id: data.artworkId,
            ...json,
            product_variant_id: variantId,
            product_type:
              parsedEditorType === 2 ? "custom_plott" : "digital_print",
            production_method: [2, 6].includes(parsedEditorType)
              ? "plotting"
              : "printing",
            material_id: FILEEDITOR[parsedEditorType].material_id,
            source: "product",
          },
          successCallback: (payload) => {
            dispatch(
              addItemToCart({
                variant_id: variantId,
                quantity: json.amount,
                options: {
                  project_uid: json.sku,
                  project_meta: JSON.stringify({
                    pid: payload?.data?.id,
                    artwork_id: data.artworkId,
                    title: json.productTitle,
                    description: configurationDescription,
                    width: json.width,
                    height: json.height,
                    thumbnail: data.skip_file_upload
                      ? false
                      : payload?.data?.attributes?.proof_preview
                      ? REACT_APP_COP_SERVER +
                        payload?.data?.attributes?.proof_preview
                      : json.preview,
                    uid: json.sku,
                    cutting: json.cutting,
                    filecheck: data.filecheck_checkbox
                      ? "filecheck"
                      : "no-filecheck",
                    vectorizeFile: json.vectorizeFile,
                    price: json.price,
                    priceBeforeDiscountAndTax: json.priceBeforeDiscountAndTax,
                    skip_file_upload: data.skip_file_upload,
                    editor: "v3",
                    source: "product",
                  }),
                },
              })
            );
          },
        })
      );
    }
  };

  return (
    <Container>
      <div className={!mobileVisible ? "d-md-none d-block" : "d-none"}>
        <p>
          {context.t(
            "Create your individual configuration and upload your design right away or later on your desktop after purchase."
          )}
        </p>
        <Button
          color="primary"
          size="lg"
          block
          onClick={() => setMobileVisible(true)}
        >
          {context.t("click here to launch configurator")} <FaCaretRight />
        </Button>
      </div>
      <div
        className={
          !mobileVisible
            ? "d-md-block d-none"
            : "inpage-configurator--mobile-visible d-block" +
              (summaryOpen
                ? " inpage-configurator--mobile-visible-summary-open"
                : "")
        }
      >
        <div
          className={`${
            mobileVisible ? "d-md-none d-block" : "d-none"
          } pull-right`}
        >
          <Button
            outline
            color="danger"
            size="sm"
            onClick={() => {
              setMobileVisible(false);
              if (scrollToConfigurator) scrollToConfigurator();
            }}
          >
            x
          </Button>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Nav
              tabs
              className="inpage-configurator--nav-bar"
              style={{ border: 0 }}
            >
              {steps.map((step, idx) => (
                <NavItem key={idx} className="mx-1">
                  <NavLink
                    style={{
                      border: 0,
                      borderBottom:
                        idx + 1 === activeTab ? "3px solid #169dce" : 0,
                    }}
                    className={`text-dark py-0 text-bold ${
                      idx + 1 === activeTab ? "active bg-light" : ""
                    }`}
                    onClick={() => {
                      setFormTouched(true);
                      setActiveTab(step.id);
                    }}
                  >
                    <strong>
                      {step.id}. {context.t(step.title)}
                    </strong>
                  </NavLink>
                </NavItem>
              ))}

              {!smallDevice && activeTab !== steps?.length && (
                <NavItem className="pl-5">
                  <Button
                    outline
                    size="sm"
                    color="primary"
                    onClick={() => continueToNextStep()}
                    className="pull-right ml-5"
                  >
                    {context.t("next step")} <FaCaretRight />
                  </Button>
                </NavItem>
              )}
            </Nav>
            <Row>
              <Col xs={12} md={8}>
                <TabContent activeTab={activeTab}>
                  {steps.map((step) => (
                    <TabPane tabId={step.id} key={step.id} className="py-4">
                      {step.type === "order" && (
                        <OrderTab editorTypeId={editorTypeId} />
                      )}
                      {step.type === "options" && (
                        <OtherOptionsTab editorTypeId={editorTypeId} />
                      )}
                      {step.type === "size" && (
                        <SizeTab
                          editorTypeId={editorTypeId}
                          updateSizes={updateSizes}
                        />
                      )}
                      {step.type === "sizeselect" && (
                        <SizeSelectTab editorTypeId={editorTypeId} />
                      )}
                      {step.type === "cutting" && (
                        <CuttingTab editorTypeId={editorTypeId} />
                      )}
                      {step.type === "file" && (
                        <FileTab
                          editorTypeId={editorTypeId}
                          mobileVisible={true || mobileVisible}
                          continueToNextStep={continueToNextStep}
                        />
                      )}
                      {step.type === "material" && (
                        <MaterialTab editorTypeId={editorTypeId} />
                      )}
                      {(smallDevice ||
                        (!smallDevice && step.id !== steps?.length)) && (
                        <>
                          <div className="inpage-configurator--step-navigator text-center mt-4">
                            {step.id !== 1 && (
                              <Button
                                color="primary"
                                outline
                                onClick={() => continueToPreviousStep()}
                                className="pull-left"
                              >
                                <FaCaretLeft /> {context.t("previous step")}
                              </Button>
                            )}
                            {(smallDevice ||
                              (!smallDevice && step.id !== steps?.length)) && (
                              <Button
                                color="primary"
                                onClick={() => continueToNextStep()}
                                className="pull-right"
                              >
                                {context.t("next step")} <FaCaretRight />
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </TabPane>
                  ))}
                  <TabPane tabId={(steps?.length || 1) + 1} className="py-4">
                    <p className="text-center text-muted">
                      {context.t(
                        "Please review your configuration below and add it to cart"
                      )}
                    </p>
                  </TabPane>
                </TabContent>
              </Col>
              <Col
                xs={12}
                md={4}
                className={`${
                  mobileVisible ? "inpage-configurator--summary" : ""
                } ${summaryOpen ? "inpage-configurator--summary-open" : ""}`}
              >
                <div
                  onClick={() => {
                    if (summaryOpen && activeTab === steps?.length + 1) {
                      continueToPreviousStep();
                    }
                    setSummaryOpen(!summaryOpen);
                  }}
                >
                  <span className="h5 pt-2 text-secondary d-block">
                    {context.t("Summary")}
                    {mobileVisible && (
                      <span className="pull-right text-primary">
                        {summaryOpen ? <FaCaretUp /> : <FaCaretDown />}
                      </span>
                    )}
                  </span>
                  <span className="h4 d-block">{productTitle}</span>
                </div>
                <p>
                  <a
                    href={`/${language}/fileguide/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={fileguideIcon}
                      alt={context.t("Production Data Guidelines")}
                      height="18"
                    />{" "}
                    {context.t("Production Data Guidelines")}
                  </a>
                </p>
                <ul className="list-group">
                  <li className="list-group-item">
                    <span className="h6 d-block mb-0">
                      {context.t("material")}
                    </span>
                    <span>
                      {context.t(
                        "materials." +
                          FILEEDITORTYPENAMES[editorTypeId] +
                          ".title"
                      )}
                      {editorData.sections.find(
                        (section) => section === "laminate"
                      ) && (
                        <>
                          ,{" "}
                          {context.t(
                            "laminates." + formData?.laminate + ".title"
                          )}
                        </>
                      )}
                    </span>
                  </li>
                  <li className="list-group-item">
                    <span className="h6 d-block mb-0">
                      {context.t("Cutting")}
                    </span>
                    <span>{context.t(formData?.cutting)}</span>
                    {formData?.cutting === "exempted" && (
                      <em className="small text-info d-block">
                        {context.t("hints.cuttings.emempted.small_elements")}
                        <br />
                        <a
                          href={`/${language}/fileguide/`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {context.t("Production Data Guidelines")}
                        </a>
                      </em>
                    )}
                  </li>
                  <li className="list-group-item">
                    <div className="h6 d-block mb-0">
                      {context.t("Size")}
                      <span
                        className="btn-link hv-pointer small pull-right"
                        style={{ fontWeight: "normal" }}
                        onClick={() => {
                          setActiveTab(1);
                          setSummaryOpen(false);
                        }}
                      >
                        {context.t("change size")}
                      </span>
                    </div>
                    <span>
                      <FormattedNumber
                        value={Math.round(formData?.width * 100) / 100}
                        language={language}
                        unit="mm"
                        decimals={1}
                      />{" "}
                      x{" "}
                      <FormattedNumber
                        value={Math.round(formData?.height * 100) / 100}
                        language={language}
                        unit="mm"
                        decimals={1}
                      />{" "}
                      (
                      <FormattedNumber
                        value={Math.round(formData?.width * 10) / 100}
                        language={language}
                        unit="cm"
                        decimals={2}
                      />{" "}
                      x{" "}
                      <FormattedNumber
                        value={Math.round(formData?.height * 10) / 100}
                        language={language}
                        unit="cm"
                        decimals={2}
                      />
                      )
                    </span>
                    {showMightBeDevidedHint && (
                      <p
                        onClick={() => setActiveTab(1)}
                        className="text-danger py-1 h6 hv-pointer"
                      >
                        ⚠️ {context.t("Print needs to be divided")}
                      </p>
                    )}
                  </li>
                  <li className="list-group-item">
                    <span className="h6 d-block mb-0">
                      {context.t("Print")}
                    </span>
                    <span>{context.t("print.description")}</span>
                  </li>
                  {/* <li className="list-group-item">
                  <span className="h6 d-block mb-0">
                    {context.t("Underglass")}
                  </span>
                  <span>Druck für Hinterglasverklebung</span>
                </li> */}
                </ul>

                <Row>
                  <Col xs={5}>
                    <FormGroup>
                      <Label for="amount">{context.t("amount")}</Label>
                      <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                          <Input
                            min="1"
                            placeholder={context.t("amount")}
                            style={{ textAlign: "center" }}
                            className="mr-1 no-spin-buttons data-hj-allow"
                            type="number"
                            step="1"
                            {...field}
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={7}>
                    <div className="h5 mt-4 pt-2">
                      <FormattedNumber
                        value={parseFloat(itemPrice?.displayPrice)}
                        language={language}
                        unit="€"
                        decimals={2}
                      />{" "}
                      / {context.t("piece")}
                    </div>
                    <div className="small text-hint">
                      {context.t("incl. VAT")} {context.t("plus shipping")}
                    </div>
                  </Col>
                </Row>
                <Button
                  className="mt-1"
                  block
                  size="lg"
                  color="success"
                  onClick={
                    showSmallHint
                      ? () => setShowSmallHintModal(true)
                      : handleSubmit(onSubmit)
                  }
                >
                  {context.t("Add to Cart")}
                </Button>
                <PaymentMethodBadges />
              </Col>
            </Row>
          </form>
        </FormProvider>
      </div>

      <Modal
        fade={false}
        isOpen={!!projectCreateError || !!artworkUploadError}
        toggle={() => {
          dispatch(resetCreateFail());
          dispatch(resetUploadFail());
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            dispatch(resetCreateFail());
            dispatch(resetUploadFail());
          }}
        >
          {context.t("Alert")}
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="d-flex justify-content-center mb-3">
              <FaTimes className="fa-fw fa-5x text-danger" />
            </div>
            <p>
              {projectCreateError &&
                context.t("We couldn't save your configuration at this time.")}
              {artworkUploadError &&
                context.t("We couldn't upload your file at this time. ")}{" "}
              {context.t(
                "This is most likely a temporary issue. Please try again in a few moments. If the problem persists, feel free to contact our support team for assistance. Thank you for your patience!"
              )}
              <br />
              <br />
              {projectCreateError && (
                <span className="small">
                  Error Details:
                  <pre>{projectCreateError}</pre>
                </span>
              )}
              {artworkUploadError && (
                <span className="small">
                  Error Details:
                  <pre>{artworkUploadError}</pre>
                </span>
              )}
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              dispatch(resetCreateFail());
              dispatch(resetUploadFail());
            }}
          >
            {context.t("Close")}
          </Button>{" "}
        </ModalFooter>
      </Modal>

      <Modal
        fade={false}
        isOpen={showSmallHintModal}
        toggle={() => {
          setShowSmallHintModal(false);
          setActiveTab(1);
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            setShowSmallHintModal(false);
            setActiveTab(1);
          }}
        >
          {context.t("Warning")}
        </ModalHeader>
        <ModalBody>
          <div>
            <div className="d-flex justify-content-center mb-3 h1">
              <FaExclamationTriangle
                className=" fa-fw fa-5x text-warning"
                aria-hidden="true"
              />
            </div>
            <p>
              {context.t(
                "The size of your design looks very small. Please double check you did not confuse MM with CM. Your selected measurements are:"
              )}
              <br />
              <br />
              <strong>
                {context.t("Size of your vinyl (width x height):")}
              </strong>
              <br />
              <span className="mr-1">
                <strong>
                  <FormattedNumber
                    value={Math.round(formData.width * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={1}
                  />
                </strong>
                &nbsp;=&nbsp;
                <FormattedNumber
                  value={Math.round(formData.width * 10) / 100}
                  language={language}
                  unit="cm"
                  decimals={2}
                />
              </span>
              x
              <span className="ml-1">
                <strong>
                  <FormattedNumber
                    value={Math.round(formData.height * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={1}
                  />
                </strong>
                &nbsp;=&nbsp;
                <FormattedNumber
                  value={Math.round(formData.height * 10) / 100}
                  language={language}
                  unit="cm"
                  decimals={2}
                />
              </span>
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            type="submit"
            onClick={handleSubmit(onSubmit)}
          >
            {context.t("Add to cart anyways")}
          </Button>{" "}
          <Button
            color="primary"
            onClick={() => {
              setShowSmallHintModal(false);
              setActiveTab(1);
              setSummaryOpen(false);
            }}
          >
            {context.t("Close and adjust")}
          </Button>{" "}
        </ModalFooter>
      </Modal>
      {(addToShopCartSuccess || addToCartProcessing) && (
        <AddToCartModal
          addItemToCart={addItemToCart}
          productCount={cart?.data?.attributes?.item_count}
          language={language}
          // FIXME maybe not the best place to do, but okay for production
          extraProducts={
            cart?.included?.filter((item) => {
              return (
                item?.type === "line_item" && item?.attributes?.sku === "7004"
              );
            }).length > 0
              ? ["text"]
              : ["text", "squeegee"]
          }
          ctaTitle={context.t("upload another design")}
          addingToCart={addingToCart}
          addedToCart={addedToCart}
          navigateTo={(url) => dispatch(push(url))}
          addToCartError={addToCartError}
          addToShopCartSuccess={addToShopCartSuccess}
          addToCartProcessing={addToCartProcessing}
          ctaAction={() => {
            resetForm();
            setMobileVisible(false);
            resetCart();
            if (scrollToConfigurator) scrollToConfigurator();
          }}
          resetCart={() => {
            resetCart();
            resetForm();
            setMobileVisible(false);
            if (scrollToConfigurator) scrollToConfigurator();
          }}
        />
      )}
    </Container>
  );
};
/**  define proptype for the 'translation' function  */
InproductConfigurator.contextTypes = {
  t: PropTypes.func,
};
export default InproductConfigurator;
